/**
 * Custom RTK Query baseQuery with:
 *   - HTTP-only cookie authentication (`credentials: 'include'`)
 *   - CSRF token injection from the `csrftoken` cookie → `X-CSRFToken` header
 *   - `X-Client-Source` / `X-Client-Version` identification headers
 *   - Single-flight 401 → sliding refresh → retry original request
 *   - 410 Gone → invalidate Post + PublicPost tags + push a warning snackbar
 *   - Refresh failure → dispatch logout + error snackbar
 */
import {
  fetchBaseQuery,
  type BaseQueryFn,
  type FetchArgs,
  type FetchBaseQueryError,
  type BaseQueryApi,
} from '@reduxjs/toolkit/query/react';

import {
  CLIENT_SOURCES,
  CLIENT_SOURCE_HEADER,
  CLIENT_VERSION_HEADER,
} from 'shared';

import { getCsrfToken } from './csrf';
import { clearUser } from '@/features/auth/authSlice';
import { enqueueNotification } from '@/features/notifications/notificationsSlice';

// ---------------------------------------------------------------------------
// Base URL resolution.
//
// Priority:
//   1. `VITE_API_BASE_URL` build-time env (set by DO App Platform in
//      staging/prod, by vitest config in tests, and optionally in .env.local).
//   2. Same-origin fallback (`window.location.origin + /api/v1`) — only
//      relevant when running behind a same-origin reverse proxy, which is NOT
//      the case in staging/prod (the static site is served from a CDN and the
//      backend lives on a separate subdomain).
//   3. `http://localhost/api/v1` for non-browser contexts.
//
// Evaluated lazily so jsdom has finished initialising window.location before
// the fallback branch reads it.
// ---------------------------------------------------------------------------

function getApiBase(): string {
  const envBase = import.meta.env.VITE_API_BASE_URL as string | undefined;
  if (envBase) return envBase;
  if (typeof window !== 'undefined' && window.location?.origin) {
    return `${window.location.origin}/api/v1`;
  }
  return 'http://localhost/api/v1';
}

// ---------------------------------------------------------------------------
// Build a fetchBaseQuery instance for the current base URL.
// Called on first use so jsdom is initialised; result is cached thereafter.
// ---------------------------------------------------------------------------

let _rawBaseQuery: ReturnType<typeof fetchBaseQuery> | null = null;

function getRawBaseQuery() {
  if (!_rawBaseQuery) {
    _rawBaseQuery = fetchBaseQuery({
      baseUrl: getApiBase(),
      credentials: 'include',
      prepareHeaders(headers) {
        const csrf = getCsrfToken();
        if (csrf) headers.set('X-CSRFToken', csrf);
        headers.set(CLIENT_SOURCE_HEADER, CLIENT_SOURCES.WEB_APP);
        headers.set(CLIENT_VERSION_HEADER, __APP_VERSION__);
        return headers;
      },
    });
  }
  return _rawBaseQuery;
}

// ---------------------------------------------------------------------------
// Single-flight refresh lock — shared across all concurrent 401s so we never
// issue more than one token-refresh request at a time.
// ---------------------------------------------------------------------------

let refreshPromise: Promise<boolean> | null = null;

/**
 * Calls the sliding-refresh endpoint and returns `true` when the server
 * issues a new access cookie, `false` on any failure.
 */
async function doRefresh(): Promise<boolean> {
  try {
    // Strip any trailing slash from the base so we never produce a double
    // slash (staging DO sets VITE_API_BASE_URL as ".../api/v1/", which would
    // otherwise yield ".../api/v1//auth/refresh-sliding/" → 404 → forced logout).
    const res = await fetch(`${getApiBase().replace(/\/+$/, '')}/auth/refresh-sliding/`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        // CSRF token must travel with the refresh request too.
        ...(getCsrfToken() ? { 'X-CSRFToken': getCsrfToken()! } : {}),
      },
    });
    return res.ok;
  } catch {
    return false;
  }
}

// ---------------------------------------------------------------------------
// Main export — wraps rawBaseQuery with auth + cache-invalidation logic.
// ---------------------------------------------------------------------------

export const baseQueryWithReauth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, apiCtx: BaseQueryApi, extraOptions) => {
  const rawBaseQuery = getRawBaseQuery();

  // --- 1. Normal request ---------------------------------------------------
  let result = await rawBaseQuery(args, apiCtx, extraOptions);

  // --- 2. 410 Gone — resource permanently deleted --------------------------
  if (result.error && result.error.status === 410) {
    // Invalidate stale Post and PublicPost entries from the RTK cache.
    // Lazy import to break the api ⇄ baseQuery circular dependency.
    try {
      const { api } = await import('./api');
      apiCtx.dispatch(api.util.invalidateTags(['Post', 'PublicPost']));
    } catch {
      // Fallback if api isn't available yet.
      apiCtx.dispatch({
        type: 'api/util/invalidateTags',
        payload: ['Post', 'PublicPost'],
      });
    }

    apiCtx.dispatch(
      enqueueNotification({
        severity: 'warning',
        message: "Ce contenu n'est plus disponible.",
        autoHideDuration: 4000,
      }),
    );

    return result;
  }

  // --- 3. 401 Unauthorized — attempt silent token refresh ------------------
  if (result.error && result.error.status === 401) {
    // Coalesce parallel 401s into a single refresh attempt.
    if (!refreshPromise) {
      refreshPromise = doRefresh().finally(() => {
        refreshPromise = null;
      });
    }

    const refreshed = await refreshPromise;

    if (refreshed) {
      // Retry the original request with the new cookie.
      result = await rawBaseQuery(args, apiCtx, extraOptions);
    } else {
      // Refresh failed — log the user out and surface an error message.
      apiCtx.dispatch(clearUser());
      apiCtx.dispatch(
        enqueueNotification({
          severity: 'error',
          message: 'Session expirée. Reconnecte-toi.',
          autoHideDuration: 5000,
        }),
      );
    }
  }

  return result;
};
